import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import {
  Dumbbell, ArrowLeft, Mail, Shield, Users, FileText,
  Scale, Cookie, AlertTriangle, Briefcase, HelpCircle,
  ChevronRight, ExternalLink
} from 'lucide-react';

// ── Shared layout for all legal/company pages ───────────────────────────────
const PageShell = ({ children, title, subtitle }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-1.5" data-testid="back-to-home">
            <ArrowLeft className="h-4 w-4" /> Home
          </Button>
          <div className="h-5 w-px bg-gray-200" />
          <Link to="/" className="flex items-center gap-1.5">
            <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-6 h-6 rounded" />
            <span className="font-bold text-gray-900 text-sm">OnlyMass</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-gray-600 text-base sm:text-lg max-w-2xl">{subtitle}</p>}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="prose prose-gray max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">
          {children}
        </div>
      </main>

      {/* Mini footer */}
      <footer className="border-t bg-white mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-500">
          <span>&copy; {new Date().getFullYear()} OnlyMass. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-gray-900">Terms</Link>
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
            <Link to="/support" className="hover:text-gray-900">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ABOUT
// ═══════════════════════════════════════════════════════════════════════════════
export const AboutPage = () => (
  <PageShell title="About OnlyMass" subtitle="The platform where fitness creators build their business and fans get real results.">
    <h2>What is OnlyMass?</h2>
    <p>
      OnlyMass is a creator-first fitness platform designed to connect fitness professionals with the people who want to train like them. We give creators the tools to monetize their expertise through subscriptions, workout plans, meal plans, and exclusive content &mdash; all in one place.
    </p>
    <p>
      For fans and fitness enthusiasts, OnlyMass is the best way to get structured, expert-designed training programs directly from the creators you follow and trust. No more piecing together random tips from social media.
    </p>

    <h2>For Creators</h2>
    <p>
      Whether you're a personal trainer, bodybuilder, yoga instructor, nutritionist, or fitness influencer, OnlyMass gives you everything you need to turn your knowledge into a sustainable business:
    </p>
    <ul>
      <li><strong>Subscriptions</strong> &mdash; Build recurring revenue with monthly subscriber access to your premium content.</li>
      <li><strong>Workout Plans</strong> &mdash; Create and sell structured workout programs with exercises, sets, reps, and media.</li>
      <li><strong>Meal Plans</strong> &mdash; Design and sell nutrition plans with detailed meals, macros, and prep instructions.</li>
      <li><strong>Posts & Media</strong> &mdash; Share photos, videos, and updates with your community.</li>
      <li><strong>Analytics</strong> &mdash; Track your growth, revenue, and engagement from a dedicated creator dashboard.</li>
      <li><strong>Payouts</strong> &mdash; Get paid directly via Stripe Connect with transparent commission rates.</li>
    </ul>

    <h2>For Fans & Subscribers</h2>
    <ul>
      <li>Follow your favorite fitness creators and see their public content for free.</li>
      <li>Subscribe for exclusive posts, programs, and behind-the-scenes content.</li>
      <li>Purchase one-time workout plans and meal plans designed by real experts.</li>
      <li>Discover new creators and niches through our curated Discover experience.</li>
      <li>Save and bookmark content to build your personal fitness library.</li>
    </ul>

    <h2>Our Mission</h2>
    <p>
      We believe fitness knowledge should be accessible, structured, and created by people who actually live the lifestyle. OnlyMass exists to empower creators to earn from their expertise while giving fans the real programs and accountability they need to reach their goals.
    </p>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// CAREERS
// ═══════════════════════════════════════════════════════════════════════════════
export const CareersPage = () => (
  <PageShell title="Careers" subtitle="Help us build the future of fitness creator monetization.">
    <h2>Join the Team</h2>
    <p>
      OnlyMass is a growing platform at the intersection of fitness, creator economy, and technology. We're always looking for talented, driven people who are passionate about building products that matter.
    </p>

    <h2>Who We Look For</h2>
    <p>We value people who are:</p>
    <ul>
      <li>Builders who ship &mdash; we move fast and focus on real user impact.</li>
      <li>Passionate about fitness, health, or the creator economy.</li>
      <li>Comfortable with ambiguity and ownership in a startup environment.</li>
      <li>Collaborative, thoughtful, and driven by craft.</li>
    </ul>

    <h2>Areas of Interest</h2>
    <p>While we don't have a formal job board yet, we're especially interested in hearing from people with experience in:</p>
    <ul>
      <li>Full-stack engineering (React, Node/Python, cloud infrastructure)</li>
      <li>Product design and UX</li>
      <li>Creator partnerships and community growth</li>
      <li>Marketing and brand strategy</li>
      <li>Content moderation and trust & safety</li>
    </ul>

    <h2>Get in Touch</h2>
    <p>
      If you're interested in working with us, we'd love to hear from you. Send a note about yourself, what excites you about OnlyMass, and any relevant work to:
    </p>
    <Card className="not-prose mt-4">
      <CardContent className="p-5 flex items-center gap-3">
        <div className="p-2.5 bg-blue-50 rounded-lg">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <p className="font-medium text-gray-900">support@onlymass.net</p>
          <p className="text-sm text-gray-500">We review every message personally.</p>
        </div>
      </CardContent>
    </Card>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SUPPORT — data-driven with clickable article links
// ═══════════════════════════════════════════════════════════════════════════════
import { SUPPORT_CATEGORIES } from './SupportCenter';

const SupportCategoryCard = ({ icon: Icon, title, description, articles }) => (
  <Card className="not-prose">
    <CardContent className="p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
      <ul className="space-y-1 ml-11">
        {articles.map((article, i) => (
          <li key={i}>
            <Link to={`/support/${article.slug}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-blue-600 py-1.5 transition-colors group" data-testid={`support-link-${article.slug}`}>
              <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
              <span className="group-hover:underline">{article.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export const SupportPage = () => (
  <PageShell title="Support" subtitle="How can we help you today?">
    <div className="not-prose grid gap-4 md:grid-cols-2 mb-8">
      {SUPPORT_CATEGORIES.map((cat, i) => (
        <SupportCategoryCard key={i} icon={cat.icon} title={cat.title} description={cat.description} articles={cat.articles} />
      ))}
    </div>

    <h2>Contact Us</h2>
    <p>
      If you can't find what you need above, reach out directly. We aim to respond within 24&ndash;48 hours on business days.
    </p>
    <Card className="not-prose mt-4">
      <CardContent className="p-5 flex items-center gap-3">
        <div className="p-2.5 bg-blue-50 rounded-lg">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <p className="font-medium text-gray-900">support@onlymass.net</p>
          <p className="text-sm text-gray-500">Please include your account email and a description of your issue.</p>
        </div>
      </CardContent>
    </Card>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// TERMS OF SERVICE
// ═══════════════════════════════════════════════════════════════════════════════
export const TermsPage = () => (
  <PageShell title="Terms of Service" subtitle="Last updated: April 2026">
    <p>
      These Terms of Service ("Terms") govern your use of OnlyMass, including our website, platform, and related services (collectively, the "Service"). By accessing or using OnlyMass, you agree to be bound by these Terms.
    </p>

    <h2>1. Accounts</h2>
    <p>You must be at least 18 years old to create an account. You are responsible for maintaining the security of your account credentials and for all activity under your account. You agree to provide accurate information during registration and to keep it up to date.</p>

    <h2>2. Users & Subscribers</h2>
    <p>As a user, you may browse public content, follow creators, subscribe to creator channels, and purchase workout plans, meal plans, and other digital products offered on the platform. Subscriptions are billed on a recurring basis as described at the time of purchase. You may cancel a subscription at any time; cancellation takes effect at the end of the current billing period.</p>

    <h2>3. Creators</h2>
    <p>Creators are users who publish content, offer subscriptions, and sell digital fitness products through OnlyMass. As a creator, you retain ownership of the content you create, but you grant OnlyMass a license to host, display, distribute, and promote that content on the platform as needed to operate the Service.</p>
    <p>Creators are responsible for ensuring their content complies with our <Link to="/community-guidelines">Community Guidelines</Link> and all applicable laws. OnlyMass reserves the right to remove content or suspend accounts that violate these Terms or our policies.</p>

    <h2>4. Payments & Fees</h2>
    <p>Purchases and subscriptions are processed through our payment provider (Stripe). All prices are displayed in USD unless otherwise noted. OnlyMass charges a platform commission on creator earnings, which is disclosed in the creator dashboard. Commission rates may vary based on account status and may change with reasonable notice.</p>

    <h2>5. Refunds</h2>
    <p>Refund eligibility depends on the type of purchase. Digital products (workout plans, meal plans) are generally non-refundable once accessed, as they are delivered digitally upon purchase. Subscription refund requests are handled on a case-by-case basis. Contact <a href="mailto:support@onlymass.net">support@onlymass.net</a> for assistance.</p>

    <h2>6. Prohibited Conduct</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use the Service for any unlawful purpose or to violate any laws.</li>
      <li>Post or distribute content that is harmful, threatening, harassing, defamatory, or obscene.</li>
      <li>Impersonate any person or entity, or misrepresent your affiliation.</li>
      <li>Attempt to gain unauthorized access to any part of the Service.</li>
      <li>Distribute spam, malware, or engage in any activity that disrupts the Service.</li>
      <li>Resell, redistribute, or commercially exploit purchased content without permission.</li>
      <li>Scrape, crawl, or use automated tools to access the Service without authorization.</li>
    </ul>

    <h2>7. Intellectual Property</h2>
    <p>OnlyMass's brand, logos, software, and platform design are the property of OnlyMass and may not be used without permission. Creator content remains the intellectual property of the respective creator. For copyright concerns, see our <Link to="/copyright">Copyright Policy</Link>.</p>

    <h2>8. Termination</h2>
    <p>OnlyMass may suspend or terminate your account at any time for violations of these Terms, our Community Guidelines, or for any other reason with or without notice. You may delete your account at any time through your account settings or by contacting support.</p>

    <h2>9. Disclaimers</h2>
    <p>OnlyMass provides a platform for fitness content and products. We do not provide medical advice. Workout and nutrition plans are created by independent creators and should be used at your own risk. Consult a qualified professional before starting any new fitness or nutrition program.</p>
    <p>The Service is provided "as is" without warranties of any kind, express or implied.</p>

    <h2>10. Limitation of Liability</h2>
    <p>To the maximum extent permitted by law, OnlyMass shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of the Service, including but not limited to physical injury from following fitness or nutrition content.</p>

    <h2>11. Changes to These Terms</h2>
    <p>We may update these Terms from time to time. Material changes will be communicated through the platform or via email. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>

    <h2>12. Contact</h2>
    <p>For questions about these Terms, contact us at <a href="mailto:support@onlymass.net">support@onlymass.net</a>.</p>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// PRIVACY POLICY
// ═══════════════════════════════════════════════════════════════════════════════
export const PrivacyPolicyPage = () => (
  <PageShell title="Privacy Policy" subtitle="Last updated: April 2026">
    <p>
      This Privacy Policy describes how OnlyMass ("we," "our," or "us") collects, uses, and protects your personal information when you use our platform and services.
    </p>

    <h2>1. Information We Collect</h2>
    <h3>Account Information</h3>
    <p>When you create an account, we collect your name, email address, and authentication credentials. If you sign in with Google, we receive your name, email, and profile picture from Google.</p>
    <h3>Profile Information</h3>
    <p>Information you choose to add to your profile, including bio, profile picture, cover photo, featured media, social links, and fitness niches.</p>
    <h3>Payment Information</h3>
    <p>Payment processing is handled by Stripe. We do not directly store your full credit card number. Stripe processes and secures your payment data in accordance with PCI-DSS standards. For creators, we collect Stripe Connect account information to facilitate payouts.</p>
    <h3>Content & Activity</h3>
    <p>Content you create or interact with on the platform, including posts, workout plans, meal plans, comments, likes, bookmarks, subscriptions, and purchases.</p>
    <h3>Usage Data</h3>
    <p>We collect information about how you use the Service, including page visits, feature usage, and session data, to improve the platform experience.</p>

    <h2>2. How We Use Your Information</h2>
    <ul>
      <li>To provide, maintain, and improve the Service.</li>
      <li>To process transactions and send related communications.</li>
      <li>To personalize your experience, including content discovery and recommendations.</li>
      <li>To communicate with you about your account, updates, and support requests.</li>
      <li>To enforce our Terms of Service and protect the safety of our users.</li>
      <li>To comply with legal obligations.</li>
    </ul>

    <h2>3. Cookies & Session Data</h2>
    <p>OnlyMass uses cookies and similar technologies to maintain your login session, remember your preferences, and analyze platform usage. See our <Link to="/cookies">Cookie Policy</Link> for more details.</p>

    <h2>4. Data Sharing</h2>
    <p>We do not sell your personal information. We may share data with:</p>
    <ul>
      <li><strong>Service Providers</strong> &mdash; Third parties that help us operate the platform (e.g., Stripe for payments, cloud hosting providers).</li>
      <li><strong>Creators</strong> &mdash; When you subscribe to or purchase from a creator, they may see your display name and subscription status. They do not receive your email or payment details.</li>
      <li><strong>Legal Requirements</strong> &mdash; When required by law, regulation, or legal process.</li>
    </ul>

    <h2>5. Your Controls</h2>
    <ul>
      <li><strong>Privacy Settings</strong> &mdash; Control the visibility of your profile and content through your account privacy settings.</li>
      <li><strong>Account Deletion</strong> &mdash; You may request deletion of your account and associated data by contacting support.</li>
      <li><strong>Cookie Preferences</strong> &mdash; You can manage cookie settings through your browser.</li>
    </ul>

    <h2>6. Data Security</h2>
    <p>We implement appropriate technical and organizational measures to protect your information. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.</p>

    <h2>7. Children's Privacy</h2>
    <p>OnlyMass is not intended for users under 18 years of age. We do not knowingly collect personal information from minors.</p>

    <h2>8. Changes to This Policy</h2>
    <p>We may update this Privacy Policy from time to time. We will notify you of material changes through the platform or via email.</p>

    <h2>9. Contact</h2>
    <p>For privacy-related questions or requests, contact us at <a href="mailto:support@onlymass.net">support@onlymass.net</a>.</p>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// COMMUNITY GUIDELINES
// ═══════════════════════════════════════════════════════════════════════════════
export const CommunityGuidelinesPage = () => (
  <PageShell title="Community Guidelines" subtitle="Standards for a safe, respectful fitness community.">
    <p>
      OnlyMass is a platform for fitness creators and enthusiasts. These guidelines help keep our community safe, respectful, and focused on health and fitness. All users &mdash; both creators and subscribers &mdash; are expected to follow these standards.
    </p>

    <h2>Content Standards</h2>
    <h3>Allowed Content</h3>
    <ul>
      <li>Fitness training content: workouts, exercises, form demonstrations, progress updates</li>
      <li>Nutrition content: meal plans, recipes, supplement guidance, dietary education</li>
      <li>Motivational and educational content related to health and fitness</li>
      <li>Behind-the-scenes creator content (training, prep, lifestyle)</li>
      <li>Product reviews and recommendations relevant to fitness</li>
    </ul>

    <h3>Prohibited Content</h3>
    <ul>
      <li>Content that promotes dangerous or medically irresponsible practices</li>
      <li>Harassment, bullying, threats, or hate speech</li>
      <li>Content that sexualizes minors in any way</li>
      <li>Spam, scams, or deceptive marketing</li>
      <li>Content that infringes on intellectual property rights</li>
      <li>Distribution of illegal substances or promotion of prohibited substance use</li>
      <li>Content unrelated to fitness, health, or wellness</li>
    </ul>

    <h2>Creator Responsibilities</h2>
    <ul>
      <li>Provide honest, accurate descriptions of your products and programs.</li>
      <li>Include appropriate disclaimers (e.g., "consult your doctor before starting a new program").</li>
      <li>Do not guarantee specific results from fitness or nutrition programs.</li>
      <li>Respond to subscriber and buyer questions in a reasonable timeframe.</li>
      <li>Respect the intellectual property of other creators.</li>
    </ul>

    <h2>User Behavior</h2>
    <ul>
      <li>Treat other users and creators with respect in comments and interactions.</li>
      <li>Do not share, redistribute, or resell purchased content.</li>
      <li>Report policy violations rather than engaging in retaliation.</li>
    </ul>

    <h2>Enforcement</h2>
    <p>Violations of these guidelines may result in content removal, account warnings, temporary suspension, or permanent ban, depending on severity and history. We aim to enforce consistently and fairly.</p>
    <p>To report a violation, contact <a href="mailto:support@onlymass.net">support@onlymass.net</a> with details.</p>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// COPYRIGHT / DMCA
// ═══════════════════════════════════════════════════════════════════════════════
export const CopyrightPage = () => (
  <PageShell title="Copyright & DMCA Policy" subtitle="How we handle intellectual property and infringement reports.">
    <p>
      OnlyMass respects the intellectual property rights of creators and third parties. If you believe that content on our platform infringes your copyright, you may submit a notice as described below.
    </p>

    <h2>Reporting Copyright Infringement</h2>
    <p>To submit a copyright infringement notice (DMCA takedown request), please send the following information to <a href="mailto:support@onlymass.net">support@onlymass.net</a>:</p>
    <ol>
      <li>A description of the copyrighted work you believe has been infringed.</li>
      <li>The URL or location of the allegedly infringing content on OnlyMass.</li>
      <li>Your contact information (name, email, mailing address).</li>
      <li>A statement that you have a good faith belief that the use is not authorized by the copyright owner, its agent, or the law.</li>
      <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on their behalf.</li>
      <li>Your physical or electronic signature.</li>
    </ol>

    <h2>Counter-Notification</h2>
    <p>If you believe your content was removed in error, you may submit a counter-notification including:</p>
    <ol>
      <li>Identification of the removed content and its former location.</li>
      <li>A statement under penalty of perjury that you have a good faith belief the content was removed by mistake.</li>
      <li>Your name, address, and phone number, and a statement consenting to the jurisdiction of the federal court in your district.</li>
      <li>Your physical or electronic signature.</li>
    </ol>

    <h2>Repeat Infringers</h2>
    <p>OnlyMass may terminate the accounts of users who are repeat copyright infringers.</p>

    <h2>Contact</h2>
    <p>For copyright and DMCA matters: <a href="mailto:support@onlymass.net">support@onlymass.net</a></p>
  </PageShell>
);

// ═══════════════════════════════════════════════════════════════════════════════
// COOKIE POLICY
// ═══════════════════════════════════════════════════════════════════════════════
export const CookiePolicyPage = () => (
  <PageShell title="Cookie Policy" subtitle="How OnlyMass uses cookies and similar technologies.">
    <p>
      This Cookie Policy explains how OnlyMass uses cookies and similar technologies when you visit or use our platform.
    </p>

    <h2>What Are Cookies?</h2>
    <p>Cookies are small text files stored on your device by your web browser. They help websites remember your preferences and maintain your session.</p>

    <h2>How We Use Cookies</h2>
    <h3>Essential Cookies</h3>
    <p>These are required for the platform to function. They include:</p>
    <ul>
      <li><strong>Session cookies</strong> &mdash; Keep you logged in as you navigate the platform.</li>
      <li><strong>Security cookies</strong> &mdash; Help protect your account and prevent unauthorized access.</li>
    </ul>

    <h3>Functional Cookies</h3>
    <p>These remember your preferences and settings, such as:</p>
    <ul>
      <li>Your selected theme or layout preferences.</li>
      <li>Recently viewed content for a better browsing experience.</li>
    </ul>

    <h3>Analytics Cookies</h3>
    <p>We may use analytics tools to understand how users interact with the platform. This data is aggregated and anonymized and helps us improve features, performance, and user experience.</p>

    <h2>Managing Cookies</h2>
    <p>You can control cookies through your browser settings. Most browsers allow you to block or delete cookies. Note that disabling essential cookies may prevent parts of the platform from functioning correctly.</p>

    <h2>Changes</h2>
    <p>We may update this Cookie Policy periodically. Check back for the latest version.</p>

    <h2>Contact</h2>
    <p>For questions about our use of cookies, contact <a href="mailto:support@onlymass.net">support@onlymass.net</a>.</p>
  </PageShell>
);
